<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import KpiCard from "@/components/dashboard/KpiCard";
import FSelectsearch from "@/components/Form/SelectSearch";
import FDateInput from "@/components/Form/DateInput";
import appConfig from "@/app.config";
import { getResumo } from "@/services/dashboard.service";
import { getAllLotes } from "@/services/faturamento.service";
import { pesquisarConvenio, pesquisarHospital } from "@/services/relatorios.service";
import { ocultaStatusNaoIniciado } from "@/utils/mixins.statuses";

function criarPeriodoPadrao() {
  const periodoEnd = new Date();
  const periodoStart = new Date();
  periodoStart.setDate(periodoStart.getDate() - 30);
  return { periodoStart, periodoEnd };
}

export default {
  page: {
    title: "Dashboard",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    KpiCard,
    FSelectsearch,
    FDateInput,
  },
  data() {
    const periodo = criarPeriodoPadrao();
    return {
      title: "Dashboard",
      items: [
        { text: "Go Care" },
        { text: "Dashboard", active: true },
      ],
      loading: false,
      erro: null,
      debounceTimer: null,
      filter: {
        periodoStart: periodo.periodoStart,
        periodoEnd: periodo.periodoEnd,
        hospitalId: null,
        convenioId: null,
        hospitalLabel: "",
        convenioLabel: "",
      },
      resumo: {
        totalLotes: 0,
        naoIniciados: 0,
        emAuditoria: 0,
        aguardandoConsenso: 0,
        retornoContestacao: 0,
        auditados: 0,
        totalGuias: 0,
        guiasNaoIniciadas: 0,
        guiasEmAuditoria: 0,
        guiasAguardandoConsenso: 0,
        guiasRetornoContestacao: 0,
        guiasAuditadas: 0,
        valorFaturado: 0,
        lotesComIaPendente: 0,
      },
      atalhos: [],
    };
  },
  computed: {
    podeVerIndicadores() {
      return this.$hasPerm("lotesScLotes");
    },
    podeVerUpload() {
      return this.$hasPerm("lotesUpload");
    },
    isHospitalConsenso() {
      return this.$hasPerm("lotesConsensoHospital");
    },
    isGoCareConsenso() {
      return this.$hasPerm("lotesConsensoReavaliar");
    },
    // Hospital (2) / Convênio (3): "Não iniciado" não aparece — conta como "Em auditoria"
    ocultaNaoIniciado() {
      return ocultaStatusNaoIniciado();
    },
    mostraKpiConsenso() {
      return this.isHospitalConsenso || this.isGoCareConsenso || this.podeVerIndicadores;
    },
    semDados() {
      return !this.loading && !this.erro && this.resumo.totalLotes === 0;
    },
  },
  mounted() {
    this.montarAtalhos();
    if (this.podeVerIndicadores) {
      this.carregarResumo();
    }
  },
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    montarAtalhos() {
      const links = [];

      if (this.podeVerIndicadores) {
        links.push({ label: "Lotes de faturamento", icon: "mdi mdi-clipboard-check-multiple-outline", to: "/faturamento/lotes" });
      }
      if (this.podeVerUpload) {
        links.push({ label: "Upload de lotes", icon: "mdi mdi-upload", to: "/faturamento/lote/upload" });
      }
      if (this.$hasPerm("relFaturamento")) {
        links.push({ label: "Relatório de faturamento", icon: "uil uil-file-alt", to: "/relatorio/faturamento" });
      }
      if (this.$hasPerm("relGlosas")) {
        links.push({ label: "Relatório de glosas", icon: "uil uil-file-check-alt", to: "/relatorio/glosas" });
      }
      if (this.$hasPerm("relIndicadoresIa")) {
        links.push({ label: "Indicadores de glosa da IA", icon: "uil uil-chart-pie", to: "/relatorio/indicadores-ia" });
      }
      if (this.$hasPerm("relAuditoriaRetrospectiva")) {
        links.push({ label: "Auditoria retrospectiva", icon: "uil uil-graph-bar", to: "/relatorio/auditoria-retrospectiva" });
      }
      if (this.$hasPerm("relRetrospectivaContasMedicas")) {
        links.push({ label: "Retrospectiva (contas médicas)", icon: "uil uil-notes", to: "/relatorio/retrospectiva-contas" });
      }
      if (this.$hasPerm("admUsu")) {
        links.push({ label: "Usuários", icon: "mdi mdi-account-group", to: "/sistema/usuarios" });
      }

      this.atalhos = links;
    },
    consultarConvenio(query) {
      return pesquisarConvenio(query);
    },
    consultarHospital(query) {
      return pesquisarHospital(query);
    },
    onConvenioSelect(convenio) {
      this.filter.convenioId = convenio?.id || null;
      this.filter.convenioLabel = convenio?.label || "";
      this.agendarCarregamento();
    },
    onHospitalSelect(hospital) {
      this.filter.hospitalId = hospital?.id || null;
      this.filter.hospitalLabel = hospital?.label || "";
      this.agendarCarregamento();
    },
    onPeriodoChange() {
      this.agendarCarregamento();
    },
    agendarCarregamento() {
      if (!this.podeVerIndicadores) return;
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => this.carregarResumo(), 350);
    },
    getParametros() {
      return {
        periodoStart: this.filter.periodoStart,
        periodoEnd: this.filter.periodoEnd,
        hospitalId: this.filter.hospitalId,
        convenioId: this.filter.convenioId,
      };
    },
    carregarResumo() {
      this.loading = true;
      this.erro = null;
      const params = this.getParametros();
      const promises = [getResumo(params)];
      if (this.isGoCareConsenso || (this.podeVerIndicadores && !this.isHospitalConsenso)) {
        promises.push(
          getAllLotes({
            status: 'EM_AUDITORIA',
            retornoContestacao: true,
            periodoStart: params.periodoStart,
            periodoEnd: params.periodoEnd,
            hospital: this.filter.hospitalLabel || undefined,
            convenio: this.filter.convenioLabel || undefined,
            paginacao: { pagina: 1, itensPorPagina: 1 },
          })
        );
      } else {
        promises.push(Promise.resolve(null));
      }
      return Promise.all(promises)
        .then(([resumoRes, retornoRes]) => {
          const data = resumoRes.data;
          this.resumo = {
            totalLotes: data.totalLotes ?? 0,
            naoIniciados: data.naoIniciados ?? 0,
            emAuditoria: data.emAuditoria ?? 0,
            aguardandoConsenso: data.aguardandoConsenso ?? 0,
            retornoContestacao: retornoRes
              ? (retornoRes.data?.paginacao?.totalRegistros ?? 0)
              : 0,
            auditados: data.auditados ?? 0,
            totalGuias: data.totalGuias ?? 0,
            guiasNaoIniciadas: data.guiasNaoIniciadas ?? 0,
            guiasEmAuditoria: data.guiasEmAuditoria ?? 0,
            guiasAguardandoConsenso: data.guiasAguardandoConsenso ?? 0,
            guiasRetornoContestacao: retornoRes
              ? (retornoRes.data?.paginacao?.totalGuias ?? 0)
              : 0,
            guiasAuditadas: data.guiasAuditadas ?? 0,
            valorFaturado: data.valorFaturado ?? 0,
            lotesComIaPendente: data.lotesComIaPendente ?? 0,
          };
        })
        .catch(() => {
          this.erro = "Não foi possível carregar os indicadores. Tente novamente.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    linkLotes(extra = {}) {
      const query = {};
      if (extra.status) query.status = extra.status;
      if (extra.filtroIa) query.filtroIa = extra.filtroIa;
      if (extra.aguardandoConsenso) query.aguardandoConsenso = 'true';
      if (extra.retornoContestacao) query.retornoContestacao = 'true';
      if (this.filter.periodoStart) query.periodoStart = this.filter.periodoStart.toISOString();
      if (this.filter.periodoEnd) query.periodoEnd = this.filter.periodoEnd.toISOString();
      if (this.filter.hospitalLabel) query.hospital = this.filter.hospitalLabel;
      if (this.filter.convenioLabel) query.convenio = this.filter.convenioLabel;
      return { path: "/faturamento/lotes", query };
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="podeVerIndicadores" class="card mb-3">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-sm-6 col-md-3 col-lg-2">
            <label class="form-label mb-1">Período inicial</label>
            <FDateInput
              v-model="filter.periodoStart"
              @change="onPeriodoChange"
              :key="`dt_start_${filter.periodoStart}`"
            />
          </div>
          <div class="col-sm-6 col-md-3 col-lg-2">
            <label class="form-label mb-1">Período final</label>
            <FDateInput
              v-model="filter.periodoEnd"
              @change="onPeriodoChange"
              :key="`dt_end_${filter.periodoEnd}`"
            />
          </div>
          <f-selectsearch
            label="Hospital"
            name="hospitalId"
            ref="hospitalId"
            v-model="filter.hospitalId"
            :cols="3"
            :searchFunction="consultarHospital"
            @select="onHospitalSelect"
            placeholder="Todos"
          />
          <f-selectsearch
            label="Convênio"
            name="convenioId"
            ref="convenioId"
            v-model="filter.convenioId"
            :cols="3"
            :searchFunction="consultarConvenio"
            @select="onConvenioSelect"
            placeholder="Todos"
          />
        </div>
      </div>
    </div>

    <b-alert v-if="erro" show variant="danger">{{ erro }}</b-alert>

    <div v-if="podeVerIndicadores">
      <div v-if="semDados" class="alert alert-light border text-center py-4 mb-3">
        Nenhum lote encontrado para o período e filtros selecionados.
      </div>

      <div class="row g-3 mb-4">
        <div v-if="!ocultaNaoIniciado" class="col-sm-6 col-xl-3">
          <KpiCard
            label="Não iniciados"
            :value="resumo.naoIniciados"
            value-label="lotes"
            :secondary-value="resumo.guiasNaoIniciadas"
            icon="mdi mdi-clock-outline"
            color="danger"
            :to="linkLotes({ status: 'NAO_INICIADO' })"
            :loading="loading"
          />
        </div>
        <div class="col-sm-6 col-xl-3">
          <KpiCard
            label="Em auditoria"
            :value="resumo.emAuditoria"
            value-label="lotes"
            :secondary-value="resumo.guiasEmAuditoria"
            icon="mdi mdi-clipboard-edit-outline"
            color="warning"
            :to="linkLotes({ status: 'EM_AUDITORIA' })"
            :loading="loading"
          />
        </div>
        <div v-if="mostraKpiConsenso" class="col-sm-6 col-xl-3">
          <KpiCard
            label="Aguardando consenso"
            :value="resumo.aguardandoConsenso"
            value-label="lotes"
            :secondary-value="resumo.guiasAguardandoConsenso"
            icon="mdi mdi-handshake-outline"
            color="info"
            :to="linkLotes({ status: 'AGUARDANDO_CONSENSO', aguardandoConsenso: true })"
            :loading="loading"
          />
        </div>
        <div v-if="!isHospitalConsenso && (isGoCareConsenso || podeVerIndicadores)" class="col-sm-6 col-xl-3">
          <KpiCard
            label="Retorno de contestação"
            :value="resumo.retornoContestacao"
            value-label="lotes"
            :secondary-value="resumo.guiasRetornoContestacao"
            icon="mdi mdi-backup-restore"
            color="warning"
            :to="linkLotes({ status: 'EM_AUDITORIA', retornoContestacao: true })"
            :loading="loading"
          />
        </div>
        <div class="col-sm-6 col-xl-3">
          <KpiCard
            label="Auditados"
            :value="resumo.auditados"
            value-label="lotes"
            :secondary-value="resumo.guiasAuditadas"
            icon="mdi mdi-check-circle-outline"
            color="success"
            :to="linkLotes({ status: 'AUDITADO' })"
            :loading="loading"
          />
        </div>
        <div class="col-sm-6 col-xl-3">
          <KpiCard
            label="Total de lotes"
            :value="resumo.totalLotes"
            icon="mdi mdi-package-variant-closed"
            color="primary"
            :to="linkLotes()"
            :loading="loading"
          />
        </div>
        <div class="col-sm-6 col-xl-3">
          <KpiCard
            label="Total de guias"
            :value="resumo.totalGuias"
            icon="mdi mdi-file-document-multiple-outline"
            color="info"
            :loading="loading"
          />
        </div>
        <div class="col-sm-6 col-xl-3">
          <KpiCard
            label="Valor faturado"
            :value="resumo.valorFaturado"
            format="currency"
            icon="mdi mdi-currency-usd"
            color="primary"
            :loading="loading"
          />
        </div>
        <div v-if="!isHospitalConsenso" class="col-sm-6 col-xl-3">
          <KpiCard
            label="Sugestões IA pendentes"
            :value="resumo.lotesComIaPendente"
            icon="mdi mdi-robot-outline"
            color="secondary"
            :to="linkLotes({ filtroIa: 'PENDENTES' })"
            :loading="loading"
          />
        </div>
      </div>
    </div>

    <div v-else-if="podeVerUpload" class="card mb-4">
      <div class="card-body text-center py-5">
        <i class="mdi mdi-home-variant-outline text-primary font-size-48 d-block mb-3"></i>
        <h5 class="mb-2">Bem-vindo ao Go Care</h5>
        <p class="text-muted mb-4">Envie novos lotes de faturamento para iniciar o fluxo de auditoria.</p>
        <router-link to="/faturamento/lote/upload" class="btn btn-primary">
          <i class="mdi mdi-upload me-1"></i> Ir para upload
        </router-link>
      </div>
    </div>

    <div v-else class="card mb-4">
      <div class="card-body text-center py-5">
        <i class="mdi mdi-home-variant-outline text-primary font-size-48 d-block mb-3"></i>
        <h5 class="mb-2">Bem-vindo ao Go Care</h5>
        <p class="text-muted mb-0">Utilize o menu lateral para acessar as funcionalidades disponíveis no seu perfil.</p>
      </div>
    </div>

    <div v-if="atalhos.length" class="card">
      <div class="card-body">
        <h5 class="card-title mb-3">Atalhos</h5>
        <div class="row g-2">
          <div v-for="atalho in atalhos" :key="atalho.to" class="col-sm-6 col-md-4 col-lg-3">
            <router-link :to="atalho.to" class="btn btn-outline-primary w-100 text-start">
              <i :class="[atalho.icon, 'me-2']"></i>{{ atalho.label }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
